import { useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { api } from "../../api/client";
import type { AppSettings } from "../../types";
import { Avatar } from "../conversation/TutorPanel";

interface Props {
  onComplete: () => void;
}

const LEVELS: { value: string; blurb: string }[] = [
  { value: "B1", blurb: "Handle everyday conversation, with some hesitation." },
  { value: "B2", blurb: "Discuss most topics fairly fluently." },
  { value: "C1", blurb: "Express yourself spontaneously and precisely." },
  { value: "C2", blurb: "Near-native command of the language." },
];

const SUGGESTED_TOPICS = [
  "Travel", "Food & cooking", "Cinema & TV", "Work & career",
  "Daily life", "Grammar drills", "Current events", "Art & culture",
];

const MODES: { value: "manual" | "auto"; label: string; blurb: string }[] = [
  {
    value: "manual",
    label: "Manual",
    blurb: "Tap the mic to speak, tap again when you're done. You set the pace, so there's no rush to find your words.",
  },
  {
    value: "auto",
    label: "Auto",
    blurb: "Elena hears when you stop and replies on her own. More like a real conversation, but it expects a steadier flow.",
  },
];

// Turn-taking is easier to control manually until you're comfortable improvising.
const AUTO_READY_LEVELS = ["C1", "C2"];

const HEARD_OPTIONS = [
  "Google search", "YouTube", "Reddit", "Instagram / TikTok",
  "Friend or word of mouth", "Product Hunt",
];

const FLOW = [
  { n: "1", title: "Practice", desc: "Speak with Elena in natural Italian conversations, at your level." },
  { n: "2", title: "Progress report", desc: "After each session she corrects your mistakes and rates your speaking." },
  { n: "3", title: "Exercises", desc: "Targeted drills are generated from the exact errors you made." },
];

/** First-run onboarding. Opaque full-screen overlay; collects level, topics, and
 *  turn-taking (mic) mode, then marks the user onboarded. Every step is
 *  skippable — skipping still saves whatever was chosen and closes the wizard. */
export default function OnboardingWizard({ onComplete }: Props) {
  const { user } = useUser();
  const navigate = useNavigate();

  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [level, setLevel] = useState<string | null>(null);
  const [topics, setTopics] = useState<string[]>([]);
  const [freeTopics, setFreeTopics] = useState("");
  const [micMode, setMicMode] = useState<"manual" | "auto" | null>(null);
  const [heardAbout, setHeardAbout] = useState("");
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getSettings().then(setSettings).catch(() => { /* save will no-op if absent */ });
  }, []);

  const steps = useMemo(() => ["welcome", "level", "topics", "mode", "hear", "how"], []);
  const key = steps[step];
  const isLast = step === steps.length - 1;

  const effectiveLevel = level ?? settings?.tutor_level ?? "B1";
  // Auto turn-taking suits fluent speakers; recommend manual below C1. Honour an
  // explicit pick, otherwise fall back to the level-based recommendation.
  const recommendedMode: "manual" | "auto" =
    AUTO_READY_LEVELS.includes(effectiveLevel) ? "auto" : "manual";
  const effectiveMicMode = micMode ?? recommendedMode;

  const toggleTopic = (t: string) =>
    setTopics((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const finish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (settings) {
        const merged = [...topics, freeTopics.trim()].filter(Boolean).join(", ");
        await api.updateSettings({
          ...settings,
          tutor_level: effectiveLevel,
          preferred_topics: merged,
          mic_mode: effectiveMicMode,
        });
      }
    } catch {
      /* don't block onboarding on a settings save hiccup */
    }
    try {
      await api.completeOnboarding(heardAbout);
    } catch {
      /* even if this fails, close locally so the user isn't trapped */
    }
    onComplete();
    navigate("/");
  };

  const next = () => (isLast ? void finish() : setStep((s) => s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const firstName = user?.firstName?.trim();

  return (
    <div className="fixed inset-0 z-[60] bg-bg overflow-y-auto">
      <div className="min-h-full flex items-center justify-center px-5 py-10">
        <div className="card w-full max-w-lg p-7 sm:p-9 animate-rise">
          {/* progress dots */}
          <div className="flex items-center justify-center gap-1.5 mb-8">
            {steps.map((s, i) => (
              <span
                key={s}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === step ? "w-6 bg-ink" : i < step ? "w-1.5 bg-ink/40" : "w-1.5 bg-surface2"
                }`}
              />
            ))}
          </div>

          {key === "welcome" && (
            <div className="flex flex-col items-center text-center gap-4">
              <Avatar amp={0} size={88} />
              <div>
                <h2 className="font-display font-bold text-[1.6rem] tracking-[-0.02em] text-ink leading-tight">
                  {firstName ? `Ciao, ${firstName}.` : "Ciao!"}
                </h2>
                <p className="text-[0.8125rem] text-text-tertiary italic mt-0.5">
                  Sono Elena, la tua tutor d'italiano.
                </p>
              </div>
              <p className="text-[0.9375rem] text-text-secondary leading-relaxed max-w-sm">
                Let's set up your space so I can teach the way that fits you. It
                takes about a minute, and you can change anything later in Settings.
              </p>
            </div>
          )}

          {key === "level" && (
            <div className="flex flex-col gap-5">
              <Header
                title="What's your Italian level?"
                sub="I'll match my vocabulary and pace to this."
              />
              <div className="flex flex-col gap-2">
                {LEVELS.map(({ value, blurb }) => {
                  const active = effectiveLevel === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setLevel(value)}
                      className={`flex items-center gap-3.5 text-left rounded-xl border px-4 py-3 transition-colors duration-200 cursor-pointer ${
                        active
                          ? "border-success bg-success/15"
                          : "border-border-soft hover:border-border"
                      }`}
                    >
                      <span
                        className={`flex-shrink-0 grid place-items-center w-10 h-10 rounded-full text-[0.8125rem] font-semibold ${
                          active ? "bg-success text-cream" : "bg-surface2 text-text-secondary"
                        }`}
                      >
                        {value}
                      </span>
                      <span className={`text-[0.875rem] leading-snug ${active ? "text-text-primary" : "text-text-secondary"}`}>{blurb}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-center text-text-primary font-medium">Not sure? Start at B1, I adapt as we talk.</p>
            </div>
          )}

          {key === "topics" && (
            <div className="flex flex-col gap-5">
              <Header
                title="Anything you'd love to talk about?"
                sub="Optional. I'll steer our conversations toward what you pick."
              />
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_TOPICS.map((t) => {
                  const active = topics.includes(t);
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => toggleTopic(t)}
                      className={`rounded-full border px-3.5 py-1.5 text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer ${
                        active
                          ? "border-success bg-success text-cream"
                          : "border-border-soft text-text-secondary hover:border-success hover:text-success"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <textarea
                value={freeTopics}
                placeholder="Anything else? e.g. Italian football, opera, your trip to Roma…"
                onChange={(e) => setFreeTopics(e.target.value)}
                className="input-base resize-y min-h-[80px] leading-normal rounded-[10px] w-full"
              />
            </div>
          )}

          {key === "mode" && (
            <div className="flex flex-col gap-5">
              <Header
                title="How should we take turns?"
                sub="You can switch anytime during a session, or later in Settings."
              />
              <div className="flex flex-col gap-2">
                {MODES.map(({ value, label, blurb }) => {
                  const active = effectiveMicMode === value;
                  const recommended = recommendedMode === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setMicMode(value)}
                      className={`flex flex-col gap-1 text-left rounded-xl border px-4 py-3 transition-colors duration-200 cursor-pointer ${
                        active
                          ? "border-success bg-success/15"
                          : "border-border-soft hover:border-border"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`text-[0.9375rem] font-semibold ${active ? "text-text-primary" : "text-text-secondary"}`}>
                          {label}
                        </span>
                        {recommended && (
                          <span className="rounded-full bg-success/15 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-success">
                            Recommended
                          </span>
                        )}
                      </span>
                      <span className="text-[0.8125rem] leading-snug text-text-secondary">{blurb}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-sm text-center text-text-primary font-medium">
                {recommendedMode === "manual"
                  ? "At your level, manual keeps the pace comfortable."
                  : "At C1+, auto feels more like a real conversation."}
              </p>
            </div>
          )}

          {key === "hear" && (
            <div className="flex flex-col gap-5">
              <Header
                title="How did you hear about us?"
                sub="Optional. It helps me know where to find more learners like you."
              />
              <div className="flex flex-wrap gap-2">
                {HEARD_OPTIONS.map((o) => {
                  const active = heardAbout === o;
                  return (
                    <button
                      key={o}
                      type="button"
                      onClick={() => setHeardAbout(active ? "" : o)}
                      className={`rounded-full border px-3.5 py-1.5 text-[0.8125rem] font-medium transition-colors duration-200 cursor-pointer ${
                        active
                          ? "border-success bg-success text-cream"
                          : "border-border-soft text-text-secondary hover:border-success hover:text-success"
                      }`}
                    >
                      {o}
                    </button>
                  );
                })}
              </div>
              <input
                value={heardAbout}
                placeholder="Or tell me in your own words…"
                onChange={(e) => setHeardAbout(e.target.value)}
                className="input-base rounded-[10px] w-full"
              />
            </div>
          )}

          {key === "how" && (
            <div className="flex flex-col gap-5">
              <Header
                title="Here's how it works"
                sub="A simple loop that compounds every time you practice."
              />
              <div className="flex flex-col gap-3">
                {FLOW.map(({ n, title, desc }) => (
                  <div key={n} className="flex gap-3.5">
                    <span className="flex-shrink-0 grid place-items-center w-7 h-7 rounded-full bg-success/15 text-success text-[0.8125rem] font-semibold">
                      {n}
                    </span>
                    <div>
                      <p className="text-[0.9375rem] font-semibold text-ink leading-tight">{title}</p>
                      <p className="text-[0.8125rem] text-text-secondary leading-snug mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[0.8125rem] text-text-secondary leading-relaxed">
                Your <span className="font-semibold text-ink">Vocabulary</span> and{" "}
                <span className="font-semibold text-ink">Progress</span> dashboards track it
                all over time. Change your level, topics, and more anytime in Settings.
              </p>
            </div>
          )}

          {/* controls */}
          <div className="flex items-center justify-between gap-3 mt-8">
            {step > 0 ? (
              <button type="button" onClick={back} className="btn-quiet" disabled={saving}>
                Back
              </button>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-4">
              {!isLast && (
                <button
                  type="button"
                  onClick={() => void finish()}
                  disabled={saving}
                  className="text-[0.8125rem] font-medium text-text-tertiary hover:text-ink transition-colors cursor-pointer disabled:opacity-40"
                >
                  Skip for now
                </button>
              )}
              <button type="button" onClick={next} className="btn-primary" disabled={saving}>
                {saving ? "Saving…" : isLast ? "Start practicing" : "Continue"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Header({ title, sub }: { title: string; sub: string }) {
  return (
    <div>
      <h2 className="font-display font-bold text-[1.35rem] tracking-[-0.02em] text-ink leading-tight">
        {title}
      </h2>
      <p className="text-[0.875rem] text-text-secondary mt-1 leading-relaxed">{sub}</p>
    </div>
  );
}
